import { API_BASE_URL } from '@synergy/config';
import { AxiosError } from 'axios';
import { PaloSection } from '@synergy/components-palo';
import { request } from '@synergy/api';
import { useEffect, useState } from 'react';

/**
 * List routes whose endpoints return a paginated `total`, so a nav sub-view can
 * show a real count. The frontend route segment equals the API resource path.
 */
const COUNTABLE_ROUTES = [
    '/demands',
    '/inquiries',
    '/purchase-orders',
    '/sales-orders',
    '/invoices',
    '/bonus-settlements',
];

export interface PaloNavCounts {
    /** Real counts by sub-view `to`. */
    counts: Record<string, number>;
    /** Sub-view `to`s whose filter the endpoint rejects (400) — not a valid view. */
    invalid: Set<string>;
}

/**
 * Probes the active section's countable sub-views by asking each list endpoint
 * for its `total` with limit=1 (one lightweight request per view). Returns real
 * counts and the set of sub-views the backend rejects, so the nav can both show
 * counts and hide filters that are invalid for this document type.
 */
export const usePaloNavCounts = (section?: PaloSection): PaloNavCounts => {
    const [result, setResult] = useState<PaloNavCounts>({ counts: {}, invalid: new Set() });

    useEffect(() => {
        setResult({ counts: {}, invalid: new Set() });

        if (!section) {
            return;
        }

        const items = section.groups
            .flatMap(group => group.items)
            .filter(item => COUNTABLE_ROUTES.includes(item.to.split('?')[0]));

        if (items.length === 0) {
            return;
        }

        const controller = new AbortController();

        Promise.all(
            items.map(async item => {
                const [path, query] = item.to.split('?');
                const url = `${API_BASE_URL}${path}?${query ? query + '&' : ''}limit=1`;
                try {
                    const response = await request.get(url, { signal: controller.signal });

                    return { to: item.to, count: response.data?.total as number, invalid: false };
                } catch (error) {
                    const status = (error as AxiosError)?.response?.status;

                    return { to: item.to, count: undefined, invalid: status === 400 };
                }
            })
        ).then(entries => {
            if (controller.signal.aborted) {
                return;
            }
            setResult({
                counts: Object.fromEntries(
                    entries.filter(entry => typeof entry.count === 'number').map(entry => [entry.to, entry.count])
                ),
                invalid: new Set(entries.filter(entry => entry.invalid).map(entry => entry.to)),
            });
        });

        return () => controller.abort();
    }, [section]);

    return result;
};
